import React from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import { SanityImage } from 'sanity-image';

import styles from '@rsc/ui-components/styles';
import breakpoints from '@rsc/ui-components/breakpoints';
import Button from '@rsc/ui-components/button/Button';

// eslint-disable-next-line import/no-cycle
import BlockBuilder from '@rsc/ui-components/sanity/BlockBuilder';

const sanityBaseUrl = `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`;

const Container = styled.div`
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  padding-top: ${styles.spacing['spacing-2xl']};
  padding-bottom: ${styles.spacing['spacing-2xl']};
  background-color: ${({ themeStyle }) => themeStyle?.backgroundColor};

  h2 {
    color: ${({ themeStyle }) => themeStyle?.color};
  }
`;

const Heading = styled.h2`
  margin-bottom: ${styles.spacing['spacing-lg']};
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  gap: ${styles.spacing['spacing-1xl']};
  padding: 0 ${styles.spacing['spacing-lg']} 0;
  margin: 0 auto;
  max-width: ${breakpoints.lg};

  @media (min-width: ${breakpoints.md}) {
    gap: ${styles.spacing['spacing-lg']};
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: ${breakpoints.lg}) {
    gap: ${styles.spacing['spacing-1xl']};
  }
`;

const Column = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.cardText};
  border: 1px solid ${({ theme }) => theme.colors.border};

  ul {
    margin: 0;
    padding-left: ${styles.spacing['spacing-lg']};
  }

  h3 {
    text-align: center;
  }
`;

const ContentContainer = styled.div`
  padding: ${styles.spacing['spacing-lg']} ${styles.spacing['spacing-md']};
`;

const StyledImage = styled(SanityImage)`
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
`;

function BlockBuilderThreeColumnLayout({ value, getInternalLink }) {
  const theme = useTheme();
  const {
    heading,
    themeStyle,
    firstColumnImage,
    firstColumn,
    secondColumnImage,
    secondColumn,
    thirdColumnImage,
    thirdColumn,
  } = value;

  return (
    <Container
      className="blockbuilder-three-column-layout"
      themeStyle={theme?.colors?.contentCard?.[themeStyle]}
    >
      {heading && <Heading>{heading}</Heading>}
      <Grid>
        {firstColumn?.blocks?.length > 0 && (
          <Column>
            {firstColumnImage?.asset?._id && (
              <StyledImage
                id={firstColumnImage.asset._id}
                baseUrl={sanityBaseUrl}
                alt={firstColumnImage.alt}
                loading="lazy"
                hotspot={firstColumnImage?.hotspot}
                crop={firstColumnImage?.crop}
              />
            )}
            <ContentContainer>
              <BlockBuilder
                blocks={firstColumn.blocks}
                getInternalLink={getInternalLink}
                Button={Button}
              />
            </ContentContainer>
          </Column>
        )}
        {secondColumn?.blocks?.length > 0 && (
          <Column>
            {secondColumnImage?.asset?._id && (
              <StyledImage
                id={secondColumnImage.asset._id}
                baseUrl={sanityBaseUrl}
                alt={secondColumnImage.alt}
                loading="lazy"
                hotspot={secondColumnImage?.hotspot}
                crop={secondColumnImage?.crop}
              />
            )}
            <ContentContainer>
              <BlockBuilder
                blocks={secondColumn.blocks}
                getInternalLink={getInternalLink}
                Button={Button}
              />
            </ContentContainer>
          </Column>
        )}
        {thirdColumn?.blocks?.length > 0 && (
          <Column>
            {thirdColumnImage?.asset?._id && (
              <StyledImage
                id={thirdColumnImage.asset._id}
                baseUrl={sanityBaseUrl}
                alt={thirdColumnImage.alt}
                loading="lazy"
                hotspot={thirdColumnImage?.hotspot}
                crop={thirdColumnImage?.crop}
              />
            )}
            <ContentContainer>
              <BlockBuilder
                blocks={thirdColumn.blocks}
                getInternalLink={getInternalLink}
                Button={Button}
              />
            </ContentContainer>
          </Column>
        )}
      </Grid>
    </Container>
  );
}

BlockBuilderThreeColumnLayout.propTypes = {
  value: PropTypes.shape({
    heading: PropTypes.string,
    themeStyle: PropTypes.shape({
      color: PropTypes.string,
      backgroundColor: PropTypes.string,
    }),
    firstColumnImage: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        _id: PropTypes.string,
      }),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
    }),
    firstColumn: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    secondColumnImage: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        _id: PropTypes.string,
      }),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
    }),
    secondColumn: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    thirdColumnImage: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        _id: PropTypes.string,
      }),
      hotspot: PropTypes.shape({}),
      crop: PropTypes.shape({}),
    }),
    thirdColumn: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderThreeColumnLayout;
